import React, { useMemo, useState } from 'react';
import { Popover } from '@headlessui/react';
import { Reviews } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import ChevronUp from 'svg/ChevronUp';
import { ReviewTabsEnum, ReviewType, SortCriterionType } from 'screens/CoachDetails/types';
import { getFilteredReviews, sortCriterions } from 'screens/CoachDetails/utils';
import NoDataPlaceholder from 'components/DiscoverPage/NoDataPlaceholder';
import classNames from 'styles/utils/classNames';
import Tabs from './Tabs';
import ReviewCard from '../ReviewCard';

const ReviewListing: React.FC<{ reviews: ReviewType[] }> = ({ reviews }) => {
  const [activeTab, setActiveTab] = useState<ReviewTabsEnum>(ReviewTabsEnum.All);
  const [sortCriterion, setSortCriterion] = useState<SortCriterionType>(sortCriterions[0]);

  const filteredReviews = useMemo(
    () => getFilteredReviews(reviews as Reviews[], activeTab, sortCriterion),
    [reviews, activeTab, sortCriterion],
  );

  const sortDropdown = (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button className="ml-auto flex items-center gap-2">
            <span className="typography-product-button-label-medium">{sortCriterion.label}</span>
            <ChevronUp
              className={classNames('h-4 w-4 rotate-180 transition-transform', open && '!rotate-0')}
            />
          </Popover.Button>
          <Popover.Panel className="absolute right-2 z-[20] mt-2 w-[15rem] rounded-lg border bg-white p-4 shadow-lg">
            <div className="flex flex-col gap-1">
              {sortCriterions.map((criterion) => (
                <button
                  key={criterion.value}
                  onClick={() => setSortCriterion(criterion)}
                  className={classNames(
                    'typography-product-button-label-medium border border-transparent p-ds-md text-left text-color-text-lightmode-secondary transition-all hover:border-color-bg-lightmode-icon dark:text-color-text-darkmode-secondary    hover:dark:border-color-bg-darkmode-icon ',
                    sortCriterion.value === criterion.value &&
                      '!border-color-bg-lightmode-icon dark:border-color-bg-darkmode-icon',
                  )}
                >
                  {criterion.label}
                </button>
              ))}
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  );

  return (
    <div className="flex w-full flex-col">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h3 className="typography-product-heading-desktop ">
          {reviews.length}{' '}
          {pluralize({
            singular: 'Review',
            plural: 'Reviews',
            count: reviews.length,
            hideCount: true,
          })}
        </h3>
        <div className="lg:hidden">{sortDropdown}</div>
      </div>
      <div className="flex  items-center justify-between">
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
        <div className="max-lg:hidden">{sortDropdown}</div>
      </div>
      <hr className="mt-ds-2xl" />
      {filteredReviews.length > 0 ? (
        <div className="no-scrollbar flex flex-1 flex-col gap-6 overflow-auto pb-12 pt-ds-2xl">
          {filteredReviews.map((review) => (
            <ReviewCard review={review} inModal={true} key={review.id} />
          ))}
        </div>
      ) : (
        <NoDataPlaceholder text={'No reviews available'} className="my-ds-2xl" />
      )}
    </div>
  );
};

export default ReviewListing;
